import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Shield, Lock, Mail } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Backend access page — separate visual identity from the public /login.
 * Only users with role=admin can proceed past this gate; everyone else
 * gets a generic invalid-credentials message.
 */
export default function BackendLogin() {
  const { login } = useAuth();
  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(form.email, form.password);
      if (user?.role !== 'admin') {
        // Don't leak that the account exists — sign them right back out.
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        toast.error('Invalid backend credentials');
        return;
      }
      navigate('/admin', { replace: true });
    } catch {
      toast.error('Invalid backend credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center p-4 font-mono">
      <div className="w-full max-w-sm">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-slate-800 border border-slate-700 rounded-lg mb-3">
            <Shield className="w-6 h-6 text-emerald-400" />
          </div>
          <h1 className="text-xl font-semibold tracking-wide">PhotoPlan Backend</h1>
          <p className="text-xs text-slate-500 mt-1">Restricted access</p>
        </div>

        <Card className="p-6 bg-slate-900 border-slate-800 text-slate-100">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-slate-400 text-xs uppercase tracking-wide">Email</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="pl-9 h-10 bg-slate-950 border-slate-700 text-slate-100 focus:border-emerald-500"
                  autoComplete="username"
                />
              </div>
            </div>

            <div>
              <Label className="text-slate-400 text-xs uppercase tracking-wide">Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  type="password"
                  required
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="pl-9 h-10 bg-slate-950 border-slate-700 text-slate-100 focus:border-emerald-500"
                  autoComplete="current-password"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-10 bg-emerald-600 hover:bg-emerald-500 text-white font-medium"
            >
              {loading ? 'Authenticating…' : 'Access backend'}
            </Button>
          </form>
        </Card>

        <p className="text-center mt-4 text-[11px] text-slate-600">
          Authorized personnel only.
        </p>
      </div>
    </div>
  );
}
